/**
 * Shared Mate catalog contract (C0 / Run 6)
 *
 * MateId + roster rows live in `@losi/crew-mates` (packages/crew-mates).
 * This module re-exports the catalog and owns the feed/LosiAvatar bridge
 * (etoile ↔ star, peta ↔ petal) used only by web product surfaces.
 *
 * Mobile: Metro cannot import packages/ — full roster lockstep lives at
 * mobile/lib/crew/mates.ts. Parity: crew-mates-catalog-parity.test.ts.
 *
 * Naming layers (do not collapse):
 * - Catalog MateId: petal / star / jovi (plush roster; petal display = "Peta")
 * - Engine/feed shape: peta / etoile / jovi (LosiAvatar; display = Peta / Star / Jovi)
 * - Trio peer order: CREW_TRIO_PEER_ORDER → consumed by trio.ts layout
 *
 * Preserve LosiAnimated. No Muse/Vesper.
 */

export {
  ALL_CREW_MATES,
  CREW_ANIMAL_MATES,
  CREW_SHAPES,
  DEFAULT_MATE_ID,
  isMateId,
  MATE_BY_ID,
  mateSrc,
  resolveMateId,
  type Mate,
  type MateId
} from '@losi/crew-mates'

import {
  resolveMateId as resolveSharedMateId,
  type MateId
} from '@losi/crew-mates'

/**
 * Feed/LosiAvatar shape id ↔ catalog MateId bridge.
 * Feed cards use engine shapes (`etoile`/`peta`/`jovi`); plush catalog uses
 * `star`/`petal`/`jovi`. Keep both — do not rename LosiAvatar shapes.
 */
export type CrewFeedMateShapeId = 'peta' | 'jovi' | 'etoile'

/** Alias used by feed-card / trio peers — same union as CrewFeedMateShapeId. */
export type CrewFeedMateId = CrewFeedMateShapeId

/**
 * Paint / stack order for the trio mark (back → front).
 * trio.ts CREW_TRIO_PEERS must follow this — single source for peer order.
 */
export const CREW_TRIO_PEER_ORDER = [
  'peta',
  'jovi',
  'etoile'
] as const satisfies readonly CrewFeedMateShapeId[]

/**
 * Your-crew / Feed UI labels for engine shapes.
 * Catalog + feed labels both display "Peta" for petal/peta (id stays `petal`/`peta`).
 * Display "Star" for shape id `etoile` — never surface the engine id as a label.
 */
export const CREW_FEED_SHAPE_LABELS = {
  peta: 'Peta',
  jovi: 'Jovi',
  etoile: 'Star'
} as const satisfies Record<CrewFeedMateShapeId, string>

export const CREW_FEED_SHAPE_TO_MATE: Record<CrewFeedMateShapeId, MateId> = {
  peta: 'petal',
  jovi: 'jovi',
  etoile: 'star'
}

export const CREW_FEED_MATE_TO_SHAPE: Record<
  'petal' | 'jovi' | 'star',
  CrewFeedMateShapeId
> = {
  petal: 'peta',
  jovi: 'jovi',
  star: 'etoile'
}

/** Lanes that assign a Your-crew mate — mirrors CREW_FEED_LANES in feed-card. */
export type CrewFeedLaneId =
  | 'interest'
  | 'ops'
  | 'follow_up'
  | 'calendar'
  | 'insight'

/**
 * Lane → engine shape. Labels come from CREW_FEED_SHAPE_LABELS via mateForLane.
 * Keep mobile/lib/crew/normalize.ts LANE_TO_SHAPE in lockstep (Metro cannot import this).
 * Interest lane → etoile (Star) is also documented by interest-mate-lane.ts
 * (`INTEREST_RESEARCH_MATE_ID` derives from this map).
 */
export const CREW_FEED_LANE_TO_SHAPE = {
  follow_up: 'peta',
  interest: 'etoile',
  insight: 'etoile',
  ops: 'peta',
  calendar: 'jovi'
} as const satisfies Record<CrewFeedLaneId, CrewFeedMateShapeId>

export function isCrewFeedMateShapeId(
  value: string
): value is CrewFeedMateShapeId {
  return value === 'peta' || value === 'jovi' || value === 'etoile'
}

export function feedShapeLabel(shape: CrewFeedMateShapeId): string {
  return CREW_FEED_SHAPE_LABELS[shape]
}

export function feedShapeToMateId(shape: string): MateId | null {
  if (isCrewFeedMateShapeId(shape)) {
    return CREW_FEED_SHAPE_TO_MATE[shape]
  }
  return resolveSharedMateId(shape)
}

export function mateIdToFeedShape(
  mateId: MateId
): CrewFeedMateShapeId | null {
  if (mateId === 'petal' || mateId === 'jovi' || mateId === 'star') {
    return CREW_FEED_MATE_TO_SHAPE[mateId]
  }
  return null
}

/**
 * Resolve a stored mate_id (feed shape or catalog id) to feed { id, label }.
 * Accepts `etoile` / `star` / `Star` / `peta` / `petal` / etc.
 */
export function resolveFeedMate(
  raw: string | null | undefined
): { id: CrewFeedMateShapeId; label: string } | null {
  if (!raw) return null
  const normalized = raw.trim().toLowerCase()
  if (isCrewFeedMateShapeId(normalized)) {
    return { id: normalized, label: CREW_FEED_SHAPE_LABELS[normalized] }
  }
  const catalogId = resolveSharedMateId(normalized)
  if (!catalogId) return null
  const shape = mateIdToFeedShape(catalogId)
  if (!shape) return null
  return { id: shape, label: CREW_FEED_SHAPE_LABELS[shape] }
}

/** Lane → Your-crew mate chip (id + display label). */
export function mateForLane(lane: CrewFeedLaneId): {
  id: CrewFeedMateShapeId
  label: string
} {
  const id = CREW_FEED_LANE_TO_SHAPE[lane]
  return { id, label: CREW_FEED_SHAPE_LABELS[id] }
}
